import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useUser } from '../components/UserContext';

/**
 * Custom hook that provides authentication functionality
 * @returns {Object} Authentication methods and state
 */
export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { login, logout } = useUser();

  /**
   * Sign in with Magic Link
   * @param {string} email - User email
   */
  const signInWithMagic = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authService.authenticateWithMagic(email);
      login(result.userInfo);
      if (!sessionStorage.getItem('authRedirectPath')) navigate('/analysis');
    } catch (error) {
      authService.presentAuthFailure(error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [login, navigate]);

  /**
   * Sign in with Google OAuth
   * @param {string} authorizationCode - Google authorization code
   */
  const signInWithGoogle = useCallback(async (authorizationCode) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authService.authenticateWithGoogle(authorizationCode);
      login(result.userInfo);
      if (!sessionStorage.getItem('authRedirectPath')) navigate('/analysis');
    } catch (error) {
      authService.presentAuthFailure(error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [login, navigate]);

  /**
   * Redirect to Google OAuth
   */
  const redirectToGoogle = useCallback(() => {
    const googleAuthUrl = authService.getGoogleAuthUrl();
    window.location.href = googleAuthUrl;
  }, []);



  const clearError = useCallback(() => setError(null), []);

  return {
    signInWithMagic,
    signInWithGoogle,
    redirectToGoogle,
    loading,
    error,
    clearError
  };
}; 